/**
 * Audit Log Page Component
 * Following PRD Phase 4 specifications (lines 419-496)
 */

import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { useAuditStore } from '@/stores/auditStore';
import { DashboardLayout } from '@/components/layout';
import { AuditHeader } from '@/components/audit/AuditHeader';
import { AuditFilters } from '@/components/audit/AuditFilters';
import { AuditLogTable } from '@/components/audit/AuditLogTable';
import { AuditStats } from '@/components/audit/AuditStats';
import {
  DataTableSkeleton,
  DashboardStatsGridSkeleton,
  PageHeaderSkeleton
} from '@/components/ui/skeleton-patterns';

const AuditLog: React.FC = () => {
  const {
    fetchLogs,
    subscribeToUpdates,
    unsubscribeFromUpdates,
    isLoading,
    logs,
    stats,
    hasMore
  } = useAuditStore();
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Fetch initial logs and subscribe to updates
  useEffect(() => {
    const initializeAuditData = async () => {
      setIsInitialLoading(true);
      try {
        await fetchLogs(false);
        // Temporarily disable WebSocket to prevent errors
        // subscribeToUpdates();
      } catch (error) {
        console.error('Failed to initialize audit data:', error);
      } finally {
        setTimeout(() => {
          setIsInitialLoading(false);
        }, 800);
      }
    };

    initializeAuditData();

    return () => {
      // unsubscribeFromUpdates();
    };
  }, [fetchLogs]);

  // Handle infinite scroll
  const handleLoadMore = () => {
    if (!isLoading && hasMore) {
      fetchLogs(true);
    }
  };

  if (isInitialLoading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <PageHeaderSkeleton />
          <DashboardStatsGridSkeleton />
          <Card className="p-6">
            <DataTableSkeleton 
              rows={12} 
              columns={6} 
              showHeader={true} 
              showPagination={true} 
            />
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <AuditHeader />

        {/* Statistics Cards */}
        {stats && <AuditStats stats={stats} />}

        {/* Main Content Card */}
        <Card className="p-6">
          {/* Filters */}
          <div className="mb-6">
            <AuditFilters />
          </div>

          {/* Audit Log Table */}
          <AuditLogTable 
            logs={logs}
            isLoading={isLoading}
            onLoadMore={handleLoadMore}
            hasMore={hasMore}
          />
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AuditLog;